import { useState } from "react";
import { Heart } from "lucide-react";
import { Link } from "@/components/LocalLink";
import { DOGS, LIVE_LETTER as FOUNDER_LETTER } from "@/data/catalog";
import { PageShell, usePageMeta } from "@/components/PageShell";
import { PickLink, dogName } from "@/components/HomeSections";

const LINES = {
  Ezekiel: "Presides over all meetings from a seated position of considerable gravity.",
  Kane: "A discerning palate and considerable opinions. The bone in his portrait was a prop.",
  Malakai: "Has inventoried every mushroom patch in the Kananaskis, and one purple dinosaur.",
  Ele: "No batch leaves the shelf without her inspection. Has never once been wrong.",
};

const FullLetter = () => (
  <div className="paper" data-testid="founder-letter-full" style={{ marginTop: 20, padding: "28px 30px", color: "#1C1F1E", borderRadius: 2 }}>
    <h2 className="serif" style={{ fontSize: 30, fontWeight: 300 }} data-testid="founder-letter-heading">{FOUNDER_LETTER.heading}</h2>
    {FOUNDER_LETTER.paragraphs.map((p) => <p key={p.slice(0, 24)} className="serif" style={{ fontSize: 18, marginTop: 14, color: "#1C1F1E" }}>{p}</p>)}
    <p className="serif" style={{ fontSize: 34, fontStyle: "italic", color: "#4B3226", marginTop: 18 }} data-testid="founder-letter-signature">-J<Heart aria-hidden="true" style={{ display: "inline", width: 12, height: 12, marginLeft: 6, fill: "#8B3A2E", color: "#8B3A2E" }} /></p>
    <p style={{ fontSize: 12, letterSpacing: ".2em", textTransform: "uppercase", color: "#1C1F1E" }}>{FOUNDER_LETTER.role}</p>
  </div>
);

export default function OurStory() {
  usePageMeta("Our Story · Dirty Paws Apothecary", "Founded by four dogs. Run by the two humans they allow to do paperwork.");
  const [open, setOpen] = useState(false);
  return (
    <PageShell testid="story-page" page="story" closing>
      <div className="banner"><img src="/assets/brand/four-dogs-banner.webp" alt="Engraved banner of the four founding dogs: Ezekiel, Kane, Malakai and Eleanor" data-testid="story-banner" /></div>
      <div className="wrap">
        <div className="intro">
          <div><div className="eyebrow">Our Story</div><h1>Founded by four dogs. Run by the two humans they allow to do paperwork.</h1></div>
          <div><p>It began on a muddy trail with a question about what, exactly, was in the jar. We answer that question in full, on every label, for every batch.</p><p className="muted" style={{ fontSize: 15 }}>Made with big dogs in mind. Good dogs of every size welcome.</p></div>
        </div>
        <div className="eyebrow" style={{ marginTop: 28 }}>The staff, and the jar each one keeps on the shelf</div>
        <div className="dogs" data-testid="story-dogs">
          {DOGS.map((d) => (
            <div className="panel dog" key={d.name} data-testid={`story-dog-${d.slug}`}>
              <Link to={`/founders/${d.slug}`} className="por" style={{ display: "block" }} data-testid={`story-founder-link-${d.slug}`}>
                <img src={`/assets/founders/${d.slug}-640.webp`} alt={`Portrait of ${dogName(d)}, ${d.title}`} loading="lazy" />
              </Link>
              <div className="b">
                <Link to={`/founders/${d.slug}`}><h3>{dogName(d)}</h3></Link>
                <div className="eyebrow" style={{ fontSize: 10.5 }}>{d.title}</div>
                <p>{LINES[d.name]}</p>
                <PickLink dog={d} className="pick" arrow testid={`story-pick-${d.slug}`} />
              </div>
            </div>
          ))}
        </div>
        <div className="letter" data-testid="founder-letter">
          <img src={FOUNDER_LETTER.image} alt="The founder walking a misty forest trail with her four dogs" data-testid="founder-letter-image" />
          <div><div className="eyebrow">A letter from the founder</div>
            <blockquote data-testid="founder-quote">“There is a particular cruelty in loving dogs. No matter how many years we're given, we will always wish for a few more.”</blockquote>
            <p className="muted" style={{ maxWidth: 520, marginBottom: 20 }}>Why Dirty Paws exists, in about two minutes of reading.</p>
            <button type="button" className="btn-text" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="founder-letter-toggle">{open ? "Close the letter" : "Read the full letter"}</button>
            {open && <FullLetter />}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
